/**
 * Deeplinks that hand an editor the Stacklet MCP endpoint so the user never has
 * to paste config by hand. Both are one-click installs into a local
 * application, so both are plain `<a href>`s the OS hands to the editor rather
 * than anything the console fetches.
 */

/** The server name the install links and the copyable snippets both use. */
export const MCP_SERVER_NAME = "stacklet";

/**
 * Quotes a value for a command line the user copies into a shell.
 *
 * The endpoint comes from the deployment's config, so its shape is not ours to
 * assume: an `&` in a query string would split a pasted command in two and run
 * half of it in the background. Single quotes are literal in every POSIX shell,
 * so the only character needing care is the quote itself.
 */
export function shellQuote(value: string): string {
  return `'${value.replaceAll("'", "'\\''")}'`;
}

/**
 * Cursor's install-link format: the server name travels as its own query
 * parameter and the transport config as base64-encoded JSON.
 *
 * @see https://cursor.com/docs/mcp/install-links
 */
export function buildCursorInstallLink(url: string): string {
  const config = window.btoa(JSON.stringify({ url }));

  return `cursor://anysphere.cursor-deeplink/mcp/install?name=${MCP_SERVER_NAME}&config=${encodeURIComponent(config)}`;
}

/**
 * VS Code's install format: the whole server entry, name included, as a single
 * URL-encoded JSON query string.
 */
export function buildVsCodeInstallLink(url: string): string {
  const server = JSON.stringify({
    name: MCP_SERVER_NAME,
    type: "http",
    url,
  });

  return `vscode:mcp/install?${encodeURIComponent(server)}`;
}

/**
 * The `mcp.json` block for clients that key servers under `mcpServers` and
 * default an entry to stdio — Claude Code, Claude Desktop, and most of the
 * field. `type` is spelled out because without it such a client treats the
 * entry as a command to run and the server never connects.
 */
export function buildMcpJsonConfig(url: string): string {
  return JSON.stringify(
    { mcpServers: { [MCP_SERVER_NAME]: { type: "http", url } } },
    null,
    2,
  );
}

/**
 * Cursor's `mcp.json` block. A remote server is a bare name → `{ url }` entry:
 * Cursor infers the transport from the URL, so this is the documented shape
 * rather than the one above.
 *
 * @see https://cursor.com/docs/context/mcp
 */
export function buildCursorMcpJsonConfig(url: string): string {
  return JSON.stringify(
    { mcpServers: { [MCP_SERVER_NAME]: { url } } },
    null,
    2,
  );
}

/**
 * VS Code's `mcp.json` block. VS Code keys servers under `servers`, not
 * `mcpServers`, so it cannot share a block with the other clients — it ignores
 * the entry entirely rather than failing loudly.
 *
 * @see https://code.visualstudio.com/docs/copilot/customization/mcp-servers
 */
export function buildVsCodeMcpJsonConfig(url: string): string {
  return JSON.stringify(
    { servers: { [MCP_SERVER_NAME]: { type: "http", url } } },
    null,
    2,
  );
}
